import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm, useFormState } from 'react-final-form';
import { useQuery } from '@apollo/client';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

import { useAutoscroll } from '@common/utils/hooks/useAutoScroll';
import { useScopedTranslation } from '@common/utils/hooks/useScopedTranslation';
import { MeetingTypeLabel, MeetingTypesQuery } from '@typings/api';

import Section from '../_common/Section';
import SectionHeader from '../_common/SectionHeader';
import SectionHeaderSkeleton from '../_common/SectionHeaderSkeleton';
import { MainFormFields } from '../BookingFlow';
import { MEETING_TYPES } from '../queries';

import { MeetingType } from './MeetingType';
import Skeleton from './Skeleton';

export default function MeetingTypes() {
  const { t } = useScopedTranslation();
  const meetingTypesRef = useRef<HTMLDivElement>(null);

  const { batch, change } = useForm();
  const {
    values: { leadSegmentId, listingId, meetingType, subjectId },
  } = useFormState();

  const { data: meetingTypesData, loading: meetingTypesLoading } =
    useQuery<MeetingTypesQuery>(MEETING_TYPES, {
      variables: {
        leadSegmentId,
        subjectId,
        listingId,
      },
    });

  const meetingTypes = useMemo(
    () => meetingTypesData?.meetingTypes ?? [],
    [meetingTypesData],
  );

  const handleChange = useCallback(
    (newMeetingType: string) => {
      if (newMeetingType !== meetingType) {
        batch(() => {
          change(MainFormFields.MEETING_TYPE, newMeetingType);
          change(MainFormFields.OFFICES, undefined);
          change(MainFormFields.CONTACT, undefined);
          change(MainFormFields.TIME_SLOT, undefined);
        });
      }
    },
    [batch, change, meetingType],
  );

  useAutoscroll(meetingTypesRef);

  useEffect(() => {
    if (meetingTypes.length === 1) {
      handleChange(meetingTypes[0].label);
    }
  }, [meetingTypes, handleChange]);

  if (meetingTypesLoading) {
    return (
      <Section ref={meetingTypesRef}>
        <SectionHeaderSkeleton />
        <Skeleton />
      </Section>
    );
  }

  if (meetingTypes.length === 1) {
    return null;
  }

  if (meetingTypes.length === 0) {
    return (
      <Section ref={meetingTypesRef}>
        <SectionHeader>{t('main_form.meeting_type.question')}</SectionHeader>
        <p>{t('main_form.meeting_type.not_found')}</p>
      </Section>
    );
  }

  return (
    <Section ref={meetingTypesRef}>
      <SectionHeader>{t('main_form.meeting_type.question')}</SectionHeader>
      <div className="flex flex-wrap items-stretch overflow-hidden md:flex-no-wrap md:-mx-3 lg:flex-wrap lg:-mx-4 xl:flex-no-wrap">
        {meetingTypes.map(({ label, title }) => (
          <div
            className={`md:px-3 lg:px-4 py-2 w-full flex lg:w-1/2 xl:w-1/${Math.min(
              meetingTypes.length,
              2,
            )} 2xl:max-w-lg`}
            key={title}
          >
            <MeetingType
              icon={MeetingTypeIconMap[label]}
              label={MeetingTypeLabel[label]}
              onClick={handleChange}
              selected={label === meetingType}
            />
          </div>
        ))}
      </div>
    </Section>
  );
}

export const MeetingTypeIconMap: { [key: string]: IconProp } = {
  [MeetingTypeLabel.OFFICE]: ['fas', 'handshake'],
  [MeetingTypeLabel.ON_LOCATION]: ['fas', 'map-marker-alt'],
  [MeetingTypeLabel.PHONE]: ['fas', 'phone'],
  [MeetingTypeLabel.VIDEO]: ['fas', 'video'],
};
